import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./tile.css";

// ────────────────────────────────────────────────────────────────────────────────
// 2048 – Pure React + TypeScript implementation (single file)
// No external JS dependencies; keyboard + swipe supported.
// Drop-in component: <Tile2048 />
// ────────────────────────────────────────────────────────────────────────────────

interface TileState {
  id: number;
  value: number;
}

type Board = Array<Array<TileState | null>>;
type Direction = "left" | "right" | "up" | "down";
type CreateTileFn = (value: number) => TileState;

const SIZE = 4;
const WIN_TILE = 2048;
const BEST_KEY = "bestScore_2048";

// Utils
const range = (n: number) => Array.from({ length: n }, (_, i) => i);
const createEmptyBoard = (): Board =>
  range(SIZE).map(() => Array(SIZE).fill(null));

const emptyCells = (b: Board): Array<[number, number]> => {
  const cells: Array<[number, number]> = [];
  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) {
      if (!b[r][c]) cells.push([r, c]);
    }
  }
  return cells;
};

const randomChoice = <T,>(arr: T[]): T =>
  arr[Math.floor(Math.random() * arr.length)];

const addRandomTile = (b: Board, createTile: CreateTileFn): Board => {
  const cells = emptyCells(b);
  if (!cells.length) return b;
  const [r, c] = randomChoice(cells);
  const value = Math.random() < 0.9 ? 2 : 4;
  return b.map((row, ri) =>
    row.map((tile, ci) => {
      if (ri === r && ci === c) return createTile(value);
      return tile;
    })
  );
};

const boardEquals = (a: Board, b: Board) =>
  a.every((row, i) =>
    row.every((tile, j) => {
      const other = b[i][j];
      if (tile === other) return true;
      if (!tile || !other) return tile === other;
      return tile.id === other.id && tile.value === other.value;
    })
  );

const transpose = (b: Board): Board => {
  const t: Board = createEmptyBoard();
  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) {
      t[c][r] = b[r][c];
    }
  }
  return t;
};

const reverseRows = (b: Board): Board => b.map((row) => [...row].reverse());

// compress + merge a single row to the left, return new row and gained score
const squashRowLeft = (row: Array<TileState | null>) => {
  const filtered = row.filter((tile): tile is TileState => !!tile);
  const result: Array<TileState | null> = [];
  let scoreGain = 0;
  for (let i = 0; i < filtered.length; i++) {
    const curr = filtered[i];
    const next = filtered[i + 1];
    if (next && curr.value === next.value) {
      const mergedValue = curr.value * 2;
      result.push({ ...next, value: mergedValue });
      scoreGain += mergedValue;
      i++; // skip next
    } else {
      result.push(curr);
    }
  }
  while (result.length < SIZE) result.push(null);
  return { row: result, scoreGain };
};

const moveLeft = (b: Board) => {
  let scoreGain = 0;
  const nb: Board = b.map((row) => {
    const { row: r, scoreGain: g } = squashRowLeft(row);
    scoreGain += g;
    return r;
  });
  return { board: nb, scoreGain };
};

const moveRight = (b: Board) => {
  const reversed = reverseRows(b);
  const { board: moved, scoreGain } = moveLeft(reversed);
  return { board: reverseRows(moved), scoreGain };
};

const moveUp = (b: Board) => {
  const t = transpose(b);
  const { board: moved, scoreGain } = moveLeft(t);
  return { board: transpose(moved), scoreGain };
};

const moveDown = (b: Board) => {
  const t = transpose(b);
  const { board: moved, scoreGain } = moveRight(t);
  return { board: transpose(moved), scoreGain };
};

const canMove = (b: Board): boolean => {
  if (emptyCells(b).length) return true;
  // check adjacent equals
  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) {
      const v = b[r][c];
      if (!v) continue;
      if (
        (c + 1 < SIZE && b[r][c + 1]?.value === v.value) ||
        (r + 1 < SIZE && b[r + 1][c]?.value === v.value)
      )
        return true;
    }
  }
  return false;
};

const hasWon = (b: Board) =>
  b.some((row) => row.some((tile) => tile?.value && tile.value >= WIN_TILE));

const newBoard = (createTile: CreateTileFn): Board => {
  let b: Board = createEmptyBoard();
  b = addRandomTile(addRandomTile(b, createTile), createTile);
  return b;
};

// Pretty helpers for classNames (optional minimal styling hooks)
const MAX_TILE = 16384;

const tileClass = (v: number) => {
  const capped = Math.min(v, MAX_TILE);
  return `tile2048__tile tile2048__tile--${capped}`;
};

const rawPublicUrl = process.env.PUBLIC_URL;
const normalizedPublicUrl =
  rawPublicUrl && rawPublicUrl !== "."
    ? rawPublicUrl.replace(/\/$/, "")
    : "";
const textureBase =
  normalizedPublicUrl !== ""
    ? `${normalizedPublicUrl}/resource/ceramic`
    : `/resource/ceramic`;
const TILE_TEXTURES: Record<number, string> = {
  2: `${textureBase}/tile-2.png`,
  4: `${textureBase}/tile-4.png`,
  8: `${textureBase}/tile-8.png`,
  16: `${textureBase}/tile-16.png`,
  32: `${textureBase}/tile-32.png`,
  64: `${textureBase}/tile-64.png`,
  128: `${textureBase}/tile-128.png`,
  256: `${textureBase}/tile-256.png`,
  512: `${textureBase}/tile-512.png`,
  1024: `${textureBase}/tile-1024.png`,
  2048: `${textureBase}/tile-2048.png`,
  4096: `${textureBase}/tile-4096.png`,
  8192: `${textureBase}/tile-8192.png`,
  16384: `${textureBase}/tile-16384.png`,
};

interface AdContent {
  title: string;
  description: string;
  image: string;
}

const ADS: AdContent[] = [
  {
    title: "한섬 청담 플래그쉽 스토어",
    description:
      "이도도자기는 한섬 청담 플래그쉽 매장의 전반적인 타일 인테리어를 맡아 진행했습니다. 단순한 자재 제작을 넘어, 매장의 콘셉트와 동선을 고려한 맞춤형 타일을 직접 설계하여 완성했습니다. 모든 타일은 수작업으로 성형 및 유약 처리되어, 세상에 단 하나뿐인 공간의 질감을 만들어냈습니다.",
    image: `${process.env.PUBLIC_URL || ""}/resource/ads/한섬.jpg`,
  },
  {
    title: "신세계 강남점 식품관 - 고메 시푸드",
    description:
      "신세계 강남점의 식품관 고메 시푸드 존의 타일을 제작했습니다. 산업 생산 방식이 아닌, 장인이 하나하나 손으로 완성한 타일들은 바다의 질감과 깊이를 표현하며 공간 전체를 예술 작품처럼 빚어냅니다. 빛의 각도에 따라 다르게 반사되는 유약의 질감은 오직 이도도자기만이 표현할 수 있는 고유한 아름다움입니다.",
    image: `${process.env.PUBLIC_URL || ""}/resource/ads/신세계_식품1.jpg`,
  },
  {
    title: "신세계 강남점 식품관 - 시푸드 존",
    description:
      "신세계 강남점 식품관 시푸드 존에 적용된 타일은 모두 이도도자기의 손끝에서 탄생했습니다. 바다의 결을 담은 유약 표현과 유려한 표면 질감은 공간을 더욱 풍부하고 고급스럽게 완성합니다. 공장에서 찍어낸 타일이 아닌, 장인의 감각으로 만들어진 한 점 한 점이 매장의 품격을 높여줍니다.",
    image: `${process.env.PUBLIC_URL || ""}/resource/ads/신세계_식품2.jpg`,
  },
  {
    title: "신세계 강남점 식품관 - 미트 존",
    description:
      "신세계 강남점의 미트 존은 강렬한 색감과 질감 대비로 완성된 공간입니다. 이도도자기의 타일은 육류 코너의 온도감과 질감을 시각적으로 표현하기 위해 특별히 조색된 유약으로 제작되었습니다. 손으로 빚은 표면과 유광의 미묘한 반사는 매장의 조명 아래에서 살아 움직이듯 다채롭게 빛납니다.",
    image: `${process.env.PUBLIC_URL || ""}/resource/ads/신세계_식품3.png`,
  },
  {
    title: "에르메스 - 신세계 본점",
    description:
      "이도도자기는 신세계 본점의 에르메스 매장을 위한 독창적인 형태의 세라믹 타일을 설계하고 사출 제작했습니다. 유기적인 곡선을 띠는 이형 타일은 공간에 고유의 리듬감을 더하며, 브랜드의 정교한 미학과 완벽히 조화를 이룹니다. 완벽한 균형과 수공예적 감성이 만난 공간입니다.",
    image: `${process.env.PUBLIC_URL || ""}/resource/ads/에르메스_신세계.png`,
  },
  {
    title: "에르메스 - 신세계 판교점",
    description:
      "수천 개의 5cm 원형 타일이 하나의 거대한 파사드를 이루는 신세계 판교점의 에르메스 매장 외벽은 이도도자기의 기술력으로 완성되었습니다. 각 타일은 완벽한 원형을 구현하기 위해 정밀한 금형과 손작업으로 제작되었으며, 빛과 그림자가 교차하며 만들어내는 표면의 리듬감은 매장 외관 전체에 생동감을 부여합니다.",
    image: `${process.env.PUBLIC_URL || ""}/resource/ads/에르메스_신라.jpg`,
  },
  {
    title: "에르메스 - 신라호텔점",
    description:
      "신라호텔의 품격을 담은 에르메스 매장 외벽은 이도도자기의 장인정신으로 완성되었습니다. 유려한 곡선과 깊이 있는 색조를 가진 세라믹 타일은 호텔의 고요한 분위기와 조화를 이루며, 방문객에게 차분하면서도 강렬한 인상을 남깁니다. 한 점 한 점 수작업으로 제작된 타일이 공간의 품격을 완성합니다.",
    image: `${process.env.PUBLIC_URL || ""}/resource/ads/에르메스_판교.jpg`,
  },
];

// ────────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────────

export interface Tile2048Props {
  className?: string;
}

const Tile2048: React.FC<Tile2048Props> = ({ className }) => {
  const nextId = useRef(1);
  const createTile = useCallback<CreateTileFn>(
    (value) => ({ id: nextId.current++, value }),
    []
  );

  const [board, setBoard] = useState<Board>(() => newBoard(createTile));
  const [score, setScore] = useState(0);
  const [best, setBest] = useState<number>(() => {
    if (typeof window === "undefined") return 0;
    const raw = window.localStorage.getItem(BEST_KEY);
    return raw ? Number(raw) : 0;
  });
  const [won, setWon] = useState(false);
  const [over, setOver] = useState(false);
  const [keepPlaying, setKeepPlaying] = useState(false);
  const [showAd, setShowAd] = useState(false);
  const [adCountdown, setAdCountdown] = useState(5);
  const [currentAd, setCurrentAd] = useState<AdContent | null>(null);

  // update best in localStorage
  useEffect(() => {
    if (score > best) {
      setBest(score);
      if (typeof window !== "undefined")
        window.localStorage.setItem(BEST_KEY, String(score));
    }
  }, [score, best]);

  useEffect(() => {
    if (!showAd) return;
    if (adCountdown <= 0) return;

    const timer = window.setInterval(() => {
      setAdCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [showAd, adCountdown]);

  useEffect(() => {
    if (over) {
      setCurrentAd(ADS[Math.floor(Math.random() * ADS.length)]);
      setAdCountdown(5);
      setShowAd(true);
    } else {
      setShowAd(false);
      setAdCountdown(5);
      setCurrentAd(null);
    }
  }, [over]);

  const doMove = useCallback(
    (dir: Direction) => {
      if (over) return;
      if (won && !keepPlaying) return;

      const mover =
        dir === "left"
          ? moveLeft
          : dir === "right"
          ? moveRight
          : dir === "up"
          ? moveUp
          : moveDown;

      const { board: moved, scoreGain } = mover(board);
      if (boardEquals(moved, board)) return; // no-op

      let nb = addRandomTile(moved, createTile);
      const newScore = score + scoreGain;
      const reachedWin = hasWon(nb) || hasWon(moved);
      const gameOver = !canMove(nb);

      setBoard(nb);
      setScore(newScore);
      if (reachedWin) setWon(true);
      if (gameOver) setOver(true);
    },
    [board, score, over, won, keepPlaying, createTile]
  );

  // Keyboard controls
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      let handled = true;
      switch (e.key) {
        case "ArrowLeft":
        case "a":
        case "A":
          doMove("left");
          break;
        case "ArrowRight":
        case "d":
        case "D":
          doMove("right");
          break;
        case "ArrowUp":
        case "w":
        case "W":
          doMove("up");
          break;
        case "ArrowDown":
        case "s":
        case "S":
          doMove("down");
          break;
        default:
          handled = false;
      }
      if (handled) e.preventDefault();
    };
    window.addEventListener("keydown", onKey, { passive: false });
    return () => window.removeEventListener("keydown", onKey);
  }, [doMove]);

  // Swipe controls (basic)
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const threshold = 12; // px

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const t = e.changedTouches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  }, []);

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStart.current) return;
      const s = touchStart.current;
      const t = e.changedTouches[0];
      const dx = t.clientX - s.x;
      const dy = t.clientY - s.y;
      if (Math.abs(dx) < threshold && Math.abs(dy) < threshold) return;
      if (Math.abs(dx) > Math.abs(dy)) {
        doMove(dx > 0 ? "right" : "left");
      } else {
        doMove(dy > 0 ? "down" : "up");
      }
      touchStart.current = null;
    },
    [doMove]
  );

  const restart = useCallback(() => {
    nextId.current = 1;
    setBoard(newBoard(createTile));
    setScore(0);
    setWon(false);
    setOver(false);
    setKeepPlaying(false);
    setShowAd(false);
    setAdCountdown(5);
    setCurrentAd(null);
  }, [createTile]);

  const tiles = useMemo(
    () =>
      board.flatMap((row, r) =>
        row.flatMap((tile, c) => {
          if (!tile) return [];
          return [{ tile, row: r, col: c }];
        })
      ),
    [board]
  );

  const showMessage = ((won && !keepPlaying) || over) && !showAd;

  const handleCloseAd = () => setShowAd(false);

  return (
    <div
      className={`tile2048 ${className ?? ""}`}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      role="application"
      aria-label="2048 board"
    >
      <div className="tile2048__heading">
        <h1 className="tile2048__title">2048</h1>
        <div className="tile2048__scores">
          <div className="tile2048__score" aria-live="polite">
            <span className="tile2048__score-label">SCORE</span>
            <span className="tile2048__score-value">{score}</span>
          </div>
          <div className="tile2048__best" aria-live="polite">
            <span className="tile2048__best-label">BEST</span>
            <span className="tile2048__best-value">{best}</span>
          </div>
        </div>
      </div>

      <div className="tile2048__top">
        <p className="tile2048__intro">
          Join the numbers and get to the <strong>2048 tile!</strong>
        </p>
        <button className="tile2048__restart" onClick={restart}>
          New Game
        </button>
      </div>

      <div className="tile2048__board">
        <div className={`tile2048__message ${showMessage ? "is-visible" : ""}`}>
          <p>{over ? "Game over!" : "You win!"}</p>
          <div className="tile2048__message-actions">
            {won && !keepPlaying && (
              <button
                className="tile2048__btn"
                onClick={() => setKeepPlaying(true)}
              >
                Keep going
              </button>
            )}
            <button className="tile2048__btn" onClick={restart}>
              Try again
            </button>
          </div>
        </div>

        <div className="tile2048__grid" aria-hidden>
          {range(SIZE).map((r) =>
            range(SIZE).map((c) => (
              <div className="tile2048__grid-cell" key={`c-${r}-${c}`} />
            ))
          )}
        </div>

        <div className="tile2048__tiles">
          {tiles.map(({ tile, row, col }) => {
            const cappedValue = Math.min(tile.value, MAX_TILE);
            const texture = TILE_TEXTURES[cappedValue];
            const tileStyle: React.CSSProperties & Record<string, string> = {
              "--tile-x": `${col}`,
              "--tile-y": `${row}`,
            };
            if (texture) {
              tileStyle["--tile-texture"] = `url("${texture}")`;
            }
            return (
              <div
                key={tile.id}
                className={tileClass(tile.value)}
                style={tileStyle}
              >
                {tile.value}
              </div>
            );
          })}
        </div>
      </div>

      {showAd && currentAd && (
        <div className="tile2048__adOverlay" role="dialog" aria-modal="true">
          <div className="tile2048__adCard">
            <button
              className={`tile2048__adClose ${
                adCountdown === 0 ? "is-active" : ""
              }`}
              onClick={adCountdown === 0 ? handleCloseAd : undefined}
              aria-label="광고 닫기"
            >
              {adCountdown > 0 ? adCountdown : "×"}
            </button>
            <div className="tile2048__adMedia">
              <img src={currentAd.image} alt={currentAd.title} />
            </div>
            <div className="tile2048__adCopy">
              <h2>{currentAd.title}</h2>
              <p>{currentAd.description}</p>
            </div>
          </div>
        </div>
      )}

      <p className="tile2048__howto">
        <strong className="tile2048__highlight">플레이 방법:</strong>
        방향키(또는 스와이프!)로 타일을 움직이세요. 같은 숫자가 만나면 ‘슉!’
        하나로 합쳐집니다.
      </p>
      <hr />
    </div>
  );
};

export default Tile2048;
